import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Settings, Monitor, Cpu, Code2, Globe, ArrowRight, ArrowLeft } from "lucide-react";
import { usePageSeo, BASE_URL } from "@/hooks/useSeo";
import { useEffect } from "react";
import Navigation from "@/components/Navigation";

const services = [
  {
    icon: Settings,
    title: "Prozessoptimierung",
    href: "/prozessoptimierung",
    description: "Analyse und Neugestaltung Ihrer Geschäftsprozesse für maximale Effizienz. Wir identifizieren Engpässe und implementieren bewährte Lean-Methoden.",
    features: ["Prozessanalyse & Mapping", "Lean Management", "Workflow-Automatisierung", "Change Management"],
  },
  {
    icon: Monitor,
    title: "Digitalisierung",
    href: "/digitalisierung",
    description: "Entwicklung und Umsetzung massgeschneiderter Digitalstrategien. Von der Konzeption bis zur Implementierung begleiten wir Ihre digitale Transformation.",
    features: ["Digitalstrategie", "System-Integration", "Cloud Migration", "Digital Workplace"],
  },
  {
    icon: Cpu,
    title: "KI & Automatisierung",
    href: "/ki-beratung",
    description: "Integration von künstlicher Intelligenz und intelligenter Automatisierung in Ihre Geschäftsprozesse für nachhaltige Wettbewerbsvorteile.",
    features: ["Machine Learning", "Predictive Analytics", "RPA Implementation", "Chatbots & Assistenten"],
  },
  {
    icon: Code2,
    title: "Software Entwicklung",
    href: "/software-entwicklung",
    description: "Massgeschneiderte Softwarelösungen, individuell auf Ihre Geschäftsanforderungen zugeschnitten. Von der Konzeption bis zum Go-Live.",
    features: ["Individuelle Entwicklung", "Business-Anpassung", "API-Integration", "Wartung & Support"],
  },
  {
    icon: Globe,
    title: "Web & Digital Marketing",
    href: "/web-marketing",
    description: "Content, SEO, Social & Ads automatisieren. Datengetriebene Marketingstrategien mit modernsten Tools effizient umsetzen.",
    features: ["SEO & Content", "Social Media Automation", "Paid Ads & Analytics", "Marketing Automation"],
  },
];

export default function LeistungenPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  usePageSeo({
    title: "Unsere Leistungen | LAB336",
    description: "Prozessoptimierung, Digitalisierung, KI-Beratung, Software Entwicklung und Web-Marketing: Entdecken Sie die Leistungen von LAB336 für Schweizer KMU.",
    canonical: `${BASE_URL}/leistungen`,
  });

  return (
    <div className="min-h-screen bg-white" data-testid="leistungen-page">
      <Navigation />
      <header className="bg-lab-bg-dark pt-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-16">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-lab-accent transition-colors text-sm mb-8" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" /> Zurück zur Startseite
          </Link>
          <span className="text-lab-accent text-xs font-semibold tracking-[0.25em] uppercase block mb-4">Was wir tun</span>
          <h1 className="font-playfair text-3xl lg:text-5xl font-semibold text-white leading-tight">
            Unsere Leistungen
          </h1>
          <p className="text-gray-400 mt-4 max-w-2xl text-base lg:text-lg leading-relaxed">
            Von der Prozessanalyse bis zur KI-Integration: Wir begleiten Schweizer KMU auf dem Weg zu mehr Effizienz, Automatisierung und digitalem Wachstum.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-16">
        <div className="space-y-6">
          {services.map((service, i) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.href}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
              >
                <Link
                  to={service.href}
                  className="group block p-8 border border-lab-border bg-lab-bg-light hover:border-lab-accent transition-all"
                  data-testid={`leistung-${service.href.slice(1)}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start gap-6">
                    <div className="w-14 h-14 bg-lab-primary/5 border border-lab-border flex items-center justify-center flex-shrink-0 group-hover:border-lab-accent transition-colors">
                      <Icon className="w-6 h-6 text-lab-accent" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-3">
                        <h2 className="font-playfair text-xl lg:text-2xl font-semibold text-lab-primary group-hover:text-lab-accent transition-colors">
                          {service.title}
                        </h2>
                        <ArrowRight className="w-5 h-5 text-lab-accent opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <p className="text-lab-text-muted text-sm leading-relaxed mb-4">{service.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {service.features.map(f => (
                          <span key={f} className="text-xs px-3 py-1 bg-white border border-lab-border text-lab-text-muted">{f}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-16 p-8 bg-lab-bg-dark text-center">
          <h3 className="font-playfair text-xl font-semibold text-white mb-3">Nicht sicher, welche Leistung passt?</h3>
          <p className="text-gray-400 text-sm mb-6 max-w-lg mx-auto">In einem kostenlosen 30-Minuten-Gespräch analysieren wir Ihre Situation und empfehlen die passenden Massnahmen.</p>
          <Link to="/termin" className="inline-block bg-lab-accent text-lab-primary px-8 py-3 text-sm font-semibold hover:bg-lab-accent/90 transition-colors" data-testid="leistungen-cta">
            Kostenloses Erstgespräch buchen
          </Link>
        </div>
      </main>

      <footer className="bg-lab-bg-dark border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-8 flex items-center justify-between">
          <p className="text-gray-500 text-sm">&copy; {new Date().getFullYear()} LAB 336</p>
          <Link to="/" className="text-gray-400 hover:text-lab-accent text-sm">Startseite</Link>
        </div>
      </footer>
    </div>
  );
}
